/**
 * Use Archive Management Hook
 *
 * Custom hooks for managing audit log archives with TanStack Query.
 * Handles fetching status, preview, and triggering archive operations.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { fetchArchiveStatus, fetchArchivePreview, triggerArchive } from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  ArchiveStatusResponse,
  TriggerArchiveRequest,
  TriggerArchiveResponse,
  ArchivePreviewResponse,
} from '@/types/archive-management';

/**
 * Use Archive Status
 * Fetches archive status including configuration and archived months
 *
 * @returns TanStack Query result with archive status data
 */
export function useArchiveStatus(): UseQueryResult<ArchiveStatusResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['archive', 'status'],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchArchiveStatus(token);
    },
    staleTime: 60000, // 1 minute - archive status doesn't change frequently
    gcTime: 300000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Use Archive Preview
 * Fetches preview information for a specific month's archive
 *
 * @param year - Year to preview (null to disable)
 * @param month - Month to preview (null to disable)
 * @returns TanStack Query result with archive preview data
 */
export function useArchivePreview(
  year: number | null,
  month: number | null
): UseQueryResult<ArchivePreviewResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['archive', 'preview', year, month],
    queryFn: async () => {
      if (year === null || month === null) {
        throw new Error('Year and month are required');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchArchivePreview(year, month, token);
    },
    enabled: year !== null && month !== null,
    staleTime: 30000, // 30 seconds - preview should be relatively fresh
    gcTime: 60000, // 1 minute
    retry: 1,
  });
}

/**
 * Use Trigger Archive
 * Mutation hook for triggering archive for a specific month
 *
 * @returns TanStack Mutation result
 */
export function useTriggerArchive(): UseMutationResult<
  TriggerArchiveResponse,
  Error,
  TriggerArchiveRequest
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: TriggerArchiveRequest) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return triggerArchive(request, token);
    },
    onSuccess: (_data, variables) => {
      // Invalidate archive status to refresh the list
      queryClient.invalidateQueries({ queryKey: ['archive', 'status'] });
      // Invalidate the specific preview
      queryClient.invalidateQueries({
        queryKey: ['archive', 'preview', variables.year, variables.month],
      });
    },
  });
}
